import { useMemo, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import type { EvaluationMetadata } from "@/api/pizza-checker/schemas";
import { useBackendVersion } from "@/api/backend-version";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useLocalization } from "@/context/localization-context";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { translateOptional } from "@/i18n/translate";
import {
  modelTitle,
  readEvaluationVersions,
  readSettingsAudit,
  type ModelVersion,
  type PodBuild,
  type VersionField,
} from "@/lib/evaluation-versions";
import { cn } from "@/lib/utils";

/** a backend code (model key, runs_on, field, reason) in the UI language, or the code itself */
const label = (prefix: string, code: string) => translateOptional(`${prefix}.${code}`) ?? code;

const RUNS_ON_TONE: Record<string, string> = {
  pod: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  backend: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  reused: "border-amber-500/30 bg-amber-500/10 text-amber-300",
};

function Chip({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-full border px-1.5 text-[10px] font-medium leading-4",
        className ?? "border-border bg-muted/40 text-muted-foreground",
      )}
      title={title}
    >
      {children}
    </span>
  );
}

function Row({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="grid min-w-0 gap-x-4 gap-y-0.5 sm:grid-cols-[11rem_minmax(0,1fr)]">
      <dt className="pt-px text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{name}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/** small "field value" pairs under a row: file, hash, pod build, whatever else the backend stored */
function Fields({ fields }: { fields: VersionField[] }) {
  if (fields.length === 0) return null;
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
      {fields.map((field) => (
        <span key={field.key} className="min-w-0 break-all">
          <span className="opacity-70">{label("versions.field", field.key)}</span>{" "}
          <span className="font-mono">{field.value}</span>
        </span>
      ))}
    </div>
  );
}

function modelFields(model: ModelVersion): VersionField[] {
  const fields: VersionField[] = [];
  if (model.file && model.file !== modelTitle(model)) fields.push({ key: "file", value: model.file });
  if (model.sha256) fields.push({ key: "sha256", value: model.sha256 });
  if (model.pod) fields.push({ key: "pod", value: model.pod });
  return [...fields, ...model.extra];
}

function ModelRow({ model }: { model: ModelVersion }) {
  const { t } = useLocalization();
  // a reused result shows the model of the evaluation that computed it
  const shown = model.runsOn === "reused" ? model.reusedFrom : model;
  const title = shown ? modelTitle(shown) : null;

  return (
    <Row name={label("versions.model", model.key)}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {model.unavailable ? (
          <>
            <span className="text-xs text-muted-foreground">{t("versions.notRun")}</span>
            <Chip className="border-red-500/30 bg-red-500/10 text-red-300">
              {label("versions.unavailable", model.unavailable)}
            </Chip>
          </>
        ) : (
          <>
            <span className="min-w-0 break-all font-mono text-xs">
              {title ?? (model.runsOn === "reused" ? t("versions.reusedUnknown") : t("versions.unknown"))}
            </span>
            {model.runsOn && (
              <Chip
                className={RUNS_ON_TONE[model.runsOn]}
                title={model.runsOn === "reused" ? t("versions.reusedHint") : undefined}
              >
                {label("versions.runsOn", model.runsOn)}
              </Chip>
            )}
            {model.runsOn === "reused" && shown?.runsOn && (
              <Chip className={RUNS_ON_TONE[shown.runsOn]}>{label("versions.runsOn", shown.runsOn)}</Chip>
            )}
          </>
        )}
      </div>
      {shown && <Fields fields={modelFields(shown)} />}
    </Row>
  );
}

function PodRow({ pod }: { pod: PodBuild }) {
  const { t } = useLocalization();
  const fields: VersionField[] = [...(pod.git ? [{ key: "git", value: pod.git }] : []), ...pod.extra];
  return (
    <Row name={t("versions.gpuPod")}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <span className="font-mono text-xs">{pod.image ?? t("versions.unknown")}</span>
        <span className="font-mono text-[11px] text-muted-foreground">{pod.endpoint}</span>
      </div>
      <Fields fields={fields} />
    </Row>
  );
}

/**
 * Bottom of the pizza detail: what the evaluation was made with - the backend build, the runtime evaluation
 * settings and every model the pipeline used (where it ran), plus the GPU pods. Rendered from whatever the
 * evaluation stored (metadata.versions), so new or moved models need no change here.
 */
export function EvaluationVersionsCard({
  metadata,
  className,
}: {
  metadata?: EvaluationMetadata | null;
  className?: string;
}) {
  const { t, tp } = useLocalization();
  const [open, setOpen] = useLocalStorageState("pizza-admin:detail-versions-open:v1", true);
  const { data: currentBackend } = useBackendVersion();
  const versions = useMemo(() => readEvaluationVersions(metadata?.versions), [metadata?.versions]);
  const settings = useMemo(() => readSettingsAudit(metadata?.evaluation_settings), [metadata?.evaluation_settings]);

  if (!metadata) return null;

  const backendVersion = versions?.backend.version ?? null;
  const currentVersion = currentBackend?.version ?? null;
  const summary = versions
    ? [backendVersion && `BE ${backendVersion}`, tp("versions.modelCount", versions.models.length)]
        .filter(Boolean)
        .join(" · ")
    : t("versions.notRecordedShort");

  return (
    <section className={cn("rounded-xl border bg-card px-4 py-3", className)} aria-label={t("versions.title")}>
      <Collapsible open={open} onOpenChange={setOpen} className="group">
        <CollapsibleTrigger className="flex w-full min-w-0 items-center gap-2 text-left">
          <h3 className="shrink-0 text-sm font-semibold">{t("versions.title")}</h3>
          <span className="min-w-0 truncate text-xs text-muted-foreground" title={summary}>
            {summary}
          </span>
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          {versions ? (
            <dl className="mt-3 flex flex-col gap-2.5">
              <Row name={t("versions.backend")}>
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className="break-all font-mono text-xs">{backendVersion ?? t("versions.unknown")}</span>
                  {currentVersion && backendVersion && currentVersion !== backendVersion && (
                    <Chip
                      className="border-amber-500/30 bg-amber-500/10 text-amber-300"
                      title={t("versions.currentBackendHint")}
                    >
                      {t("versions.currentBackend", { version: currentVersion })}
                    </Chip>
                  )}
                </div>
                <Fields fields={versions.backend.gitSha ? [{ key: "git", value: versions.backend.gitSha }] : []} />
              </Row>
              {settings && (
                <Row name={t("versions.settings")}>
                  <span className="font-mono text-xs">
                    {settings.version !== null ? t("versions.settingsVersion", { version: settings.version }) : "–"}
                  </span>
                  <span className="ml-2 text-[11px] text-muted-foreground">
                    {settings.overrides > 0
                      ? tp("versions.overrides", settings.overrides)
                      : t("versions.settingsDefaults")}
                  </span>
                </Row>
              )}
              {versions.models.map((model) => (
                <ModelRow key={model.key} model={model} />
              ))}
              {versions.pods.map((pod) => (
                <PodRow key={pod.endpoint} pod={pod} />
              ))}
            </dl>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">{t("versions.notRecorded")}</p>
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}
